import { createContext, type PropsWithChildren, useState } from 'react'

import useTranslate from '../hooks/use-translate'
import {
  downloadDocuments as downloadDocumentsApi,
  DownloadDocumentsInput,
  DownloadDocumentsOptions,
  DownloadDocumentsResult
} from '../utils/files/download-documents'
import { showAxiosError } from '../utils/functions/misc.functions'

type DownloadDocumentsOptionsType = DownloadDocumentsOptions & { showLoader?: boolean }

type DownloadDocumentsContextType = {
  downloadDocuments: <T extends object | undefined = undefined>(
    input: DownloadDocumentsInput<T>,
    options?: DownloadDocumentsOptionsType
  ) => Promise<DownloadDocumentsResult>
  isLoading: boolean
}

export const DownloadDocumentsContext = createContext<DownloadDocumentsContextType>({
  downloadDocuments: async () =>
    Promise.resolve({
      fileLink: undefined,
      downloadFn: () => {},
      cleanup: () => {}
    }),
  isLoading: false
})

const DownloadDocumentsProvider = ({ children }: PropsWithChildren) => {
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslate()

  async function downloadDocuments<T extends object | undefined = undefined>(
    input: DownloadDocumentsInput<T>,
    options?: DownloadDocumentsOptionsType
  ): Promise<DownloadDocumentsResult> {
    const { showLoader = true } = options ?? {}

    let res: DownloadDocumentsResult = {
      downloadFn: () => {},
      fileLink: undefined,
      cleanup: () => {}
    }

    if (showLoader) setIsLoading(true) // Start loading
    try {
      res = await downloadDocumentsApi<T>(input, options)
      return res
    } catch (error) {
      showAxiosError(error as Error)
    } finally {
      if (showLoader) setIsLoading(false) // End loading in both cases (success or error)
      return res
    }
  }

  return (
    <DownloadDocumentsContext.Provider value={{ downloadDocuments, isLoading }}>
      {children}
      {isLoading && (
        <div>
          <div className="absolute top-0 left-0 w-full h-full bg-white bg-opacity-50 flex items-center justify-center z-50">
            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
          </div>
          <p className="absolute top-32 left-0 w-full h-full flex items-center justify-center z-50">
            {t('downloadDocuments.loading')}
          </p>
        </div>
      )}
    </DownloadDocumentsContext.Provider>
  )
}

export default DownloadDocumentsProvider
